import {
  BaseEndpointBuilder,
  endpointBuilder,
} from '@squareup/dex-data-shared-base-api';
import {
  GetApiEventDetailsResponse,
  GetApiEventsResponse,
  GetEventDetailsInput,
  GetEventsInput,
  GetFilterSearchInput,
  GetFilterSearchResponse,
  GetFiltersInput,
  GetFiltersResponse,
  GetWebhookEventDetailsResponse,
  GetWebhookEventsResponse,
} from '@squareup/dex-types-shared-api-webhook-logs';
import { convertKeysToCamelCase } from '@squareup/dex-utils-shared-object-key-utils';

import { DEVELOPER_API_REDUCER_PATH } from './developer-api-constants';
import { DeveloperApiTagTypes } from './developer-api-tag-types';
import { buildLogEventQueryParams } from './log-inspector-utils';

const LOG_INSPECTOR_API_URL = '/api/metrics';
const appName = 'log-inspector';

const logInspectorEndpointFactory = (
  builder: BaseEndpointBuilder<typeof DEVELOPER_API_REDUCER_PATH>
) => ({
  getApiEvents: builder.query<
    GetApiEventsResponse,
    GetEventsInput & {
      filters?: Map<string, string[]>;
      filtersObject?: Record<string, string[]>;
    }
  >({
    query: (params) => {
      const { filters, filtersObject, ...queryParams } = params;
      const path = `${LOG_INSPECTOR_API_URL}/api-events`;
      // Convert plain object to Map if needed
      const filtersMap =
        filters ||
        (filtersObject ? new Map(Object.entries(filtersObject)) : undefined);
      const query = buildLogEventQueryParams(queryParams, filtersMap);

      return {
        path: `${path}?${query.toString()}`,
        appName,
        useCache: true,
      };
    },
    transformResponse: (response) => {
      return convertKeysToCamelCase(response);
    },
    providesTags: [DeveloperApiTagTypes.ApiEvents],
  }),

  getApiEventDetails: builder.query<
    GetApiEventDetailsResponse,
    GetEventDetailsInput
  >({
    query: ({ client_id, id, sandbox }: GetEventDetailsInput) => {
      const path = `${LOG_INSPECTOR_API_URL}/api-events/details/${id}`;
      const query = new URLSearchParams();

      query.set('client_id', client_id);

      if (sandbox !== undefined) {
        query.set('sandbox', sandbox.toString());
      }

      return {
        path: `${path}?${query.toString()}`,
        appName,
        useCache: true,
      };
    },
    transformResponse: (response) => {
      return convertKeysToCamelCase(response);
    },
    providesTags: [DeveloperApiTagTypes.ApiEvents],
  }),

  getWebhookEvents: builder.query<
    GetWebhookEventsResponse,
    GetEventsInput & {
      filters?: Map<string, string[]>;
      filtersObject?: Record<string, string[]>;
    }
  >({
    query: (params) => {
      const { filters, filtersObject, ...queryParams } = params;
      const path = `${LOG_INSPECTOR_API_URL}/webhook-events`;
      // Convert plain object to Map if needed
      const filtersMap =
        filters ||
        (filtersObject ? new Map(Object.entries(filtersObject)) : undefined);
      const query = buildLogEventQueryParams(queryParams, filtersMap);

      return {
        path: `${path}?${query.toString()}`,
        appName,
        useCache: true,
      };
    },
    transformResponse: (response) => {
      return convertKeysToCamelCase(response);
    },
    providesTags: [DeveloperApiTagTypes.WebhookEvents],
  }),

  getWebhookEventDetails: builder.query<
    GetWebhookEventDetailsResponse,
    GetEventDetailsInput
  >({
    query: ({ client_id, id, sandbox }: GetEventDetailsInput) => {
      const path = `${LOG_INSPECTOR_API_URL}/webhook-events/details/${id}`;
      const query = new URLSearchParams();

      query.set('client_id', client_id);

      if (sandbox !== undefined) {
        query.set('sandbox', sandbox.toString());
      }

      return {
        path: `${path}?${query.toString()}`,
        appName,
        useCache: true,
      };
    },
    transformResponse: (response) => {
      return convertKeysToCamelCase(response);
    },
    providesTags: [DeveloperApiTagTypes.WebhookEvents],
  }),

  getApiEventFilters: builder.query<GetFiltersResponse, GetFiltersInput>({
    query: ({ client_id, sandbox, endpoint_category }: GetFiltersInput) => {
      const path = `${LOG_INSPECTOR_API_URL}/api-events/filters`;
      const query = new URLSearchParams();

      query.set('client_id', client_id);

      if (sandbox !== undefined) {
        query.set('sandbox', sandbox.toString());
      }

      if (endpoint_category && endpoint_category.length > 0) {
        query.set('endpoint_category', endpoint_category.join(','));
      }

      return {
        path: `${path}?${query.toString()}`,
        appName,
        useCache: true,
      };
    },
    transformResponse: (response) => {
      return convertKeysToCamelCase(response);
    },
    providesTags: [DeveloperApiTagTypes.ApiEvents],
  }),

  getWebhookEventFilters: builder.query<GetFiltersResponse, GetFiltersInput>({
    query: ({ client_id, sandbox, category }: GetFiltersInput) => {
      const path = `${LOG_INSPECTOR_API_URL}/webhook-events/filters`;
      const query = new URLSearchParams();

      query.set('client_id', client_id);

      if (sandbox !== undefined) {
        query.set('sandbox', sandbox.toString());
      }

      if (category && category.length > 0) {
        query.set('category', category.join(','));
      }

      return {
        path: `${path}?${query.toString()}`,
        appName,
        useCache: true,
      };
    },
    transformResponse: (response) => {
      return convertKeysToCamelCase(response);
    },
    providesTags: [DeveloperApiTagTypes.WebhookEvents],
  }),

  searchApiEventFilterValues: builder.query<
    GetFilterSearchResponse,
    GetFilterSearchInput
  >({
    query: ({
      client_id,
      term,
      query: searchQuery,
      sandbox,
    }: GetFilterSearchInput) => {
      const path = `${LOG_INSPECTOR_API_URL}/api-events/filters/query`;
      const query = new URLSearchParams();

      query.set('client_id', client_id);
      query.set('term', term);
      query.set('query', searchQuery);

      if (sandbox !== undefined) {
        query.set('sandbox', sandbox.toString());
      }

      return {
        path: `${path}?${query.toString()}`,
        appName,
        useCache: true,
      };
    },
    transformResponse: (response) => {
      return convertKeysToCamelCase(response);
    },
    providesTags: [DeveloperApiTagTypes.ApiEvents],
  }),

  searchWebhookEventFilterValues: builder.query<
    GetFilterSearchResponse,
    GetFilterSearchInput
  >({
    query: ({
      client_id,
      term,
      query: searchQuery,
      sandbox,
    }: GetFilterSearchInput) => {
      const path = `${LOG_INSPECTOR_API_URL}/webhook-events/filters/query`;
      const query = new URLSearchParams();

      query.set('client_id', client_id);
      query.set('term', term);
      query.set('query', searchQuery);

      if (sandbox !== undefined) {
        query.set('sandbox', sandbox.toString());
      }

      return {
        path: `${path}?${query.toString()}`,
        appName,
        useCache: true,
      };
    },
    transformResponse: (response) => {
      return convertKeysToCamelCase(response);
    },
    providesTags: [DeveloperApiTagTypes.WebhookEvents],
  }),
});

const logInspectorEndpoints = logInspectorEndpointFactory(endpointBuilder);

export { logInspectorEndpointFactory, logInspectorEndpoints };
